/**
 * The cookie-consent decision, without any opinion about how it looks.
 *
 * Extracted from `CampaignConsent.jsx` so the `/c/` banner and the `/lp/`
 * banner cannot drift apart on the three rules that are easy to get wrong and
 * invisible when wrong:
 *
 *  1. The stored choice is read SYNCHRONOUSLY into the initial state. Reading
 *     it in an effect means the bar paints for one frame and then vanishes for
 *     everyone who already decided, which looks like a bug and trains people to
 *     ignore it.
 *  2. No container configured means no banner. A cookie notice on a page that
 *     sets no cookies is noise, and it invites a "yes" that means nothing.
 *  3. The choice is recorded only when GRANTED. Firing an analytics event to
 *     report that someone declined analytics is the exact thing they declined.
 *
 * The two banners share this and differ only in markup, because `/lp/*` is
 * built entirely from inline styles with its own palette and has no `.cmp-page`
 * ancestor, so the `/c/` component's CSS variables resolve to nothing there.
 */

import { useCallback, useState } from 'react';

import { isConfigured } from './gtm';
import { readConsent, setConsent } from './consent';

export function useConsentChoice() {
  // Rule 1: synchronous initial read, no flash.
  const [choice, setChoice] = useState(readConsent);

  const decide = useCallback((next) => {
    setConsent(next);
    setChoice(next);
  }, []);

  // Rule 2 and the already-decided case.
  const visible = isConfigured() && choice !== 'granted' && choice !== 'denied';

  return {
    visible,
    choice,
    accept: useCallback(() => decide('granted'), [decide]),
    decline: useCallback(() => decide('denied'), [decide]),
  };
}

export default useConsentChoice;
